import { useState } from "react";
import { Link } from "react-router";

import { EmptyState } from "../lib/EmptyState.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { STUDIO_COPY } from "./estate.constants.ts";
import { RegisterStudioDialog } from "./RegisterStudioDialog.tsx";
import { useRegisterStudioController } from "./useRegisterStudioController.ts";
import { useStudioController } from "./useStudioController.ts";
import type { StudioDraft } from "./studio.model.ts";
import "./estate.css";

/**
 * The studio's book. Cards over the owner's works, each with the private
 * record and an inline edit — the whole record replaced on save, never a
 * field patched — and, top right, the door a new work comes in by.
 *
 * Two tabs, as drawn. "Data import" is a placeholder that says so; the
 * argument is in its copy.
 */
export function Studio({
  workBase,
  studioOndoId,
  canRegister,
}: {
  workBase: string;
  /** The workspace's subject — what the register door names the studio by. */
  studioOndoId: string;
  /** The admin rung, decided by the workspace; the database decides again. */
  canRegister: boolean;
}) {
  const { view, thumbs, editing, refusal, saving, openEdit, editDraft, closeEdit, save, refresh } =
    useStudioController(workBase);
  const register = useRegisterStudioController(studioOndoId, refresh);
  const [tab, setTab] = useState<"records" | "import">("records");

  const dialog = canRegister && (
    <RegisterStudioDialog
      view={register.view}
      workBase={workBase}
      onEdit={register.edit}
      onNext={register.next}
      onBack={register.back}
      onAddImages={register.addImages}
      onRemoveImage={register.removeImage}
      onSetImageKind={register.setImageKind}
      onSubmit={() => void register.submit()}
      onCancel={register.cancel}
      onClose={register.closeDialog}
      onRegisterAnother={register.registerAnother}
    />
  );

  const registerButton = canRegister && (
    <button className="estate__register" onClick={register.openDialog} type="button">
      {STUDIO_COPY.register}
    </button>
  );

  const tabs = (
    <div className="estate__tabs" role="tablist">
      {(["records", "import"] as const).map((id) => (
        <button
          key={id}
          aria-selected={tab === id}
          className={`estate__tab${tab === id ? " estate__tab--open" : ""}`}
          onClick={() => setTab(id)}
          role="tab"
          type="button"
        >
          {id === "records" ? STUDIO_COPY.recordsTab : STUDIO_COPY.importTab}
        </button>
      ))}
    </div>
  );

  if (tab === "import") {
    return (
      <>
        {tabs}
        <EmptyState className="estate__empty" lead={STUDIO_COPY.importLead} detail={STUDIO_COPY.importDetail} />
      </>
    );
  }

  if (view.kind === "loading") {
    return (
      <>
        {tabs}
        <p className="estate__note" role="status">
          {view.message}
        </p>
      </>
    );
  }

  if (view.kind === "error") {
    return (
      <>
        {tabs}
        <section className="estate__note estate__note--error" role="alert">
          <h2 className="estate__note-title">{view.title}</h2>
          <p>{view.message}</p>
        </section>
      </>
    );
  }

  if (view.kind === "empty") {
    return (
      <>
        {tabs}
        <div className="estate__toolbar estate__toolbar--studio">
          <span />
          {registerButton}
        </div>
        <EmptyState className="estate__empty" lead={view.lead} detail={view.detail} />
        {dialog}
      </>
    );
  }

  return (
    <>
      {tabs}
      <div className="estate__toolbar estate__toolbar--studio">
        <p className="estate__summary">{view.summary}</p>
        {registerButton}
      </div>

      <ul className="estate__cards">
        {view.rows.map((row) => (
          <li className="estate__card" key={row.workId}>
            <header className="estate__card-head">
              <span className="estate__thumb">
                <WorkThumb imageUrl={thumbs.get(row.workId) ?? null} title={row.title} />
              </span>
              <span className="estate__row-body">
                <Link className="estate__row-title" to={row.href}>
                  {row.title}
                </Link>
                <span className="estate__row-meta">{row.meta}</span>
              </span>
              <span className="estate__card-badges">
                {row.lifecycle && (
                  <span
                    className={`estate__badge estate__badge--${row.lifecycle.replace("_", "-")}`}
                  >
                    {STUDIO_COPY.lifecycle[row.lifecycle]}
                  </span>
                )}
                {row.editionLine && <span className="estate__badge">{row.editionLine}</span>}
                {row.neverSold && (
                  <span className="estate__badge estate__badge--green">
                    {STUDIO_COPY.neverSold}
                  </span>
                )}
              </span>
            </header>

            {editing?.workId === row.workId ? (
              <RecordForm
                draft={editing.draft}
                onCancel={closeEdit}
                onEdit={editDraft}
                onSave={() => void save()}
                refusal={refusal}
                saving={saving}
              />
            ) : (
              <>
                <dl className="estate__fields">
                  <Field label={STUDIO_COPY.cost} value={row.cost} />
                  <Field label={STUDIO_COPY.insured} value={row.insured} />
                  <Field label={STUDIO_COPY.retail} value={row.retail} />
                  <Field label={STUDIO_COPY.series} value={row.series} />
                  <Field label={STUDIO_COPY.location} value={row.location} />
                  <Field label={STUDIO_COPY.framing} value={row.framing} />
                </dl>
                {row.consignedTo && (
                  <p className="estate__row-meta">
                    {STUDIO_COPY.withGallery(row.consignedTo)}
                  </p>
                )}
                <button
                  className="estate__ghost"
                  onClick={() => openEdit(row.workId)}
                  type="button"
                >
                  {STUDIO_COPY.edit}
                </button>
              </>
            )}
          </li>
        ))}
      </ul>

      <p className="estate__footer">{STUDIO_COPY.footer}</p>
      {dialog}
    </>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="estate__field">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** The whole record as a form. Text inputs for money — the model parses. */
function RecordForm({
  draft,
  refusal,
  saving,
  onEdit,
  onCancel,
  onSave,
}: {
  draft: StudioDraft;
  refusal: string | null;
  saving: boolean;
  onEdit: (patch: Partial<StudioDraft>) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const field = (
    label: string,
    key: keyof StudioDraft,
    placeholder = "",
  ) => (
    <label className="estate__input">
      <span>{label}</span>
      <input
        onChange={(event) => onEdit({ [key]: event.target.value })}
        placeholder={placeholder}
        value={draft[key]}
      />
    </label>
  );

  return (
    <form
      className="estate__form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <div className="estate__form-grid">
        {field(STUDIO_COPY.cost, "cost", "42,000")}
        {field(STUDIO_COPY.insured, "insured", "180,000")}
        {field(STUDIO_COPY.retail, "retail", "240,000")}
        {field(STUDIO_COPY.series, "series", "1,680,000")}
        {field(STUDIO_COPY.currency, "currency", "GBP")}
        {field(STUDIO_COPY.framing, "framing")}
        {field(STUDIO_COPY.produced, "produced", "3")}
      </div>
      {refusal && (
        <p className="estate__refusal" role="alert">
          {refusal}
        </p>
      )}
      <span className="estate__form-actions">
        <button className="estate__ghost" onClick={onCancel} type="button">
          {STUDIO_COPY.cancel}
        </button>
        <button className="estate__primary" disabled={saving} type="submit">
          {STUDIO_COPY.save}
        </button>
      </span>
    </form>
  );
}
